import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { getItemWithExpiration } from '../lib/utils';
import {
    BROWSER_DECISION_KEYS,
    BrowserDecisionKey,
    DEFAULT_LOCKED_SECTIONS,
    EMPTY_SERVER_LOCK_DECISIONS,
    LockSupportState,
    ServerLockDecisions,
    clearClientLockDecision,
    isDecisionLockSection,
    isScheduleActionSection,
    isLockSectionLocked,
    lockSupportOfResponse,
    mergeClientLockDecisions,
    mergeServerLockDecisions,
    normalizeLockedSections,
    readClientLockDecisions,
    readServerLockDecisions,
    recordClientLockDecisions,
} from '../lib/lockedSections';

const SESSION_DURATION_MS = 30 * 60 * 1000; // 30분
const STORAGE_KEY = 'payrollAuthUnlockedAt';
const LOCKED_SECTIONS_KEY = 'lockedTabConfig';

type PayrollAuthContextType = {
    /** 잠금 섹션이 현재 인증 유효 상태인지 확인 (30분 이내) */
    isSectionUnlocked: (section: string) => boolean;
    /** 현재 인증 상태 여부 */
    isUnlocked: boolean;
    /** 마지막으로 인증된 섹션 */
    lastSection: string | null;
    /** 인증 완료 시 호출 — 모든 잠금 섹션을 동시에 해제 */
    unlockSection: (section: string) => void;
    /** 즉시 잠금 — sessionStorage 제거 + state 초기화 */
    lockSection: () => void;
    /** lastSection 직접 설정 */
    setLastSection: (section: string | null) => void;
    /** 해당 섹션이 잠금 대상인지 확인 */
    isSectionLocked: (section: string) => boolean;
    /** 잠금 대상 섹션 목록 */
    lockedSections: string[];
    /** 잠금 대상 섹션 목록 업데이트 (서버 응답 반영용 — 클라이언트 전용 키는 자동 승계) */
    setLockedSections: (sections: string[]) => void;
    /** 서버가 모르는 섹션의 잠금만 이 브라우저에 기록하고 즉시 반영 */
    applyClientLockDecisions: (sections: string[]) => void;
    /**
     * 이 화면이 보고 있는 계정을 알린다.
     *
     * 이 provider 는 라우터·App 바깥에 있어 로그인·로그아웃으로 다시 그려지지 않는다.
     * 알려 주지 않으면 B 원장 화면에 A 원장의 잠금 상태가 그대로 남는다.
     * 잠금 가드가 화면을 열기 전에 부른다.
     */
    syncAccount: (accountId?: string | null) => void;
    /**
     * 지금 계정의 잠금 계약 지원 상태(unknown / v3 / legacy).
     *
     * **부를 때** 현재 계정과 비교한다 — 판정한 계정과 다르면 그 렌더부터 unknown 이다
     * (계정 전환 뒤 effect 가 돌기 전의 첫 렌더에서 앞 계정의 v3 를 물려받지 않게).
     */
    getLockSupport: () => LockSupportState;
    /**
     * 잠금 조회·저장 결과를 알린다. 매 응답마다 지원 상태를 다시 판정한다.
     *  · data     — 응답 본문(lockVersion ≥ 3 이면 v3, 아니면 legacy)
     *  · error    — 조회 실패 → legacy (서버가 응답을 못 줄 때 화면을 영원히 막지 않는다)
     *  · inactive — 조회 대상 아님(슈퍼관리자 등) → legacy (원래 동작)
     * 지금 계정이 아닌 계정의 결과는 버린다(늦게 온 응답).
     */
    reportLockFetch: (
        accountId: string | null | undefined,
        result: { kind: 'data'; body: unknown } | { kind: 'error' } | { kind: 'inactive' },
    ) => void;
    /** 지원 상태 원본 (이관 effect 의 의존성용 — 판정에는 getLockSupport 를 쓴다) */
    lockSupport: LockSupport;
    /**
     * 이관을 한 번 맡는다. 페이지 수명 동안 (계정, 키) 당 처음 한 번만 true.
     * usePayrollAuth 가 여러 화면에서 동시에 떠 있어도 이관 요청은 한 번만 나간다.
     */
    claimLockMigration: (accountId: string, key: BrowserDecisionKey) => boolean;
};

export type LockSupport = {
    /** 이 판정이 어느 계정 것인가 */
    accountId: string | null;
    state: LockSupportState;
    /** 서버 결정 원값 (v3 일 때만 의미) */
    decisions: ServerLockDecisions;
};

const PayrollAuthContext = createContext<PayrollAuthContextType | null>(null);

/** sessionStorage에서 인증 시각 읽기 */
const getStoredUnlockTime = (): number | null => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? Number(raw) : null;
    } catch {
        return null;
    }
};

/** sessionStorage에 인증 시각 저장 */
const setStoredUnlockTime = (timestamp: number) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, String(timestamp));
    } catch {
        // sessionStorage 접근 불가 시 무시
    }
};

/**
 * 지금 로그인한 관리자.
 *
 * 잠금 결정을 계정별로 나누기 위해서만 쓴다. 센터 데스크 PC 는 여러 계정이
 * 같은 브라우저를 쓰기 때문에, 한 곳에 모아 두면 A 의 결정이 B 에게 적용된다.
 * 읽지 못하면 공용 칸을 쓴다 — 그 칸은 기본값(브리핑=잠금)만 적용된다.
 */
const currentAccountId = (): string | null => {
    try {
        const authObject = getItemWithExpiration('authObject');
        const adminId = authObject?.adminId;
        return adminId ? String(adminId) : null;
    } catch {
        return null;
    }
};

/** localStorage에 저장돼 있던 목록 (서버가 모르는 키의 예전 결정을 승계하는 데도 쓴다) */
const getRawStoredLockedSections = (): string[] => {
    try {
        const raw = localStorage.getItem(LOCKED_SECTIONS_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return normalizeLockedSections(parsed);
        }
    } catch {}
    return DEFAULT_LOCKED_SECTIONS;
};

/**
 * 화면이 실제로 쓸 잠금 목록.
 *
 * 서버가 모르는 섹션(기프티콘·데일리 브리핑)은 이 브라우저의 결정을 얹는다.
 * 결정이 없으면 예전 저장분을 승계하고, 그마저 없으면 기본값
 * (데일리 브리핑 = 잠금) 을 따른다. lockedSections.ts 주석 참고.
 */
const getStoredLockedSections = (): string[] => {
    const stored = getRawStoredLockedSections();
    return mergeClientLockDecisions(stored, readClientLockDecisions(currentAccountId()), stored);
};

/** localStorage에 잠금 섹션 목록 저장 */
const setStoredLockedSections = (sections: string[]) => {
    try {
        localStorage.setItem(LOCKED_SECTIONS_KEY, JSON.stringify(sections));
    } catch {}
};

export const PayrollAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [unlockedAt, setUnlockedAt] = useState<number | null>(() => getStoredUnlockTime());
    const [lastSection, setLastSection] = useState<string | null>(null);
    const [lockedSections, setLockedSectionsState] = useState<string[]>(() => getStoredLockedSections());
    /** 잠금 계약 지원 상태 — 계정과 묶어 둔다 (lockedSections.ts 「서버 저장(새 계약 3)」) */
    const [support, setSupport] = useState<LockSupport>(() => ({
        accountId: currentAccountId(),
        state: 'unknown',
        decisions: EMPTY_SERVER_LOCK_DECISIONS,
    }));

    /**
     * 이 상태가 **어느 계정 것인가.**
     *
     * 이 provider 는 라우터 바깥에 있어 로그아웃·계정 전환에도 언마운트되지 않는다.
     * 다시 읽지 않으면 B 원장 화면에 A 원장의 잠금 상태가 그대로 남는다.
     * (특히 로그아웃은 localStorage 를 통째로 비우므로, 남은 state 는 근거 없는 값이다)
     */
    const accountRef = useRef<string | null>(currentAccountId());
    const syncAccount = useCallback((accountId?: string | null) => {
        const account = accountId ? String(accountId) : currentAccountId();
        if (account === accountRef.current) return;
        accountRef.current = account;
        setLockedSectionsState(getStoredLockedSections());
        /* 계약 지원 상태도 이 계정 것으로 — 아직 조회 전이면 unknown (이미 이 계정 판정이 있으면 그대로) */
        setSupport((prev) =>
            prev.accountId === account
                ? prev
                : { accountId: account, state: 'unknown', decisions: EMPTY_SERVER_LOCK_DECISIONS },
        );
    }, []);
    /* 저장소를 직접 보는 경로도 남겨 둔다 (가드가 없는 화면에서 계정이 바뀐 경우) */
    useEffect(() => {
        syncAccount();
    });

    const getLockSupport = useCallback((): LockSupportState => {
        const account = currentAccountId();
        /* 계정이 없으면 잠금 조회 자체가 일어날 수 없다(조회 조건에 adminId 가 필요) — 원래 동작 */
        if (!account) return 'legacy';
        return support.accountId === account ? support.state : 'unknown';
    }, [support]);

    const reportLockFetch = useCallback<PayrollAuthContextType['reportLockFetch']>((accountId, result) => {
        const account = accountId ? String(accountId) : null;
        // 지금 계정이 아닌 결과(로그아웃·전환 뒤 늦게 온 응답)는 버린다
        if (account !== currentAccountId()) return;
        if (account !== accountRef.current) {
            // 앞 계정의 목록이 남아 있다 — syncAccount 와 같이 이 계정 것으로 먼저 되돌린다
            accountRef.current = account;
            setLockedSectionsState(getStoredLockedSections());
        }
        if (result.kind !== 'data') {
            /*
             * 조회 실패(error)는 legacy(구서버 성공)와 구분한다 — 스케줄 동작 세 키는 실패면 잠김,
             * 구서버면 열림이다. 손실 시그널·기프티콘은 둘을 같게 본다(기존 fallback 그대로).
             * 조회 대상 아님(inactive)은 원래 동작 = legacy.
             */
            setSupport({
                accountId: account,
                state: result.kind === 'error' ? 'error' : 'legacy',
                decisions: EMPTY_SERVER_LOCK_DECISIONS,
            });
            return;
        }
        const sections = (result.body as { lockedSections?: unknown } | null | undefined)?.lockedSections;
        if (!Array.isArray(sections)) {
            // 본문 없는 성공(구 api 래퍼가 삼킨 오류 등) — 판정 근거가 없다. 실패로 본다
            setSupport({ accountId: account, state: 'error', decisions: EMPTY_SERVER_LOCK_DECISIONS });
            return;
        }
        const state = lockSupportOfResponse(result.body);
        const browser = readClientLockDecisions(account);
        if (state === 'v3') {
            const decisions = readServerLockDecisions(result.body);
            /*
             * 서버가 결정을 가진 키는 이 계정의 브라우저 결정을 지운다(이관 완료·다른 기기가 먼저 정함).
             * 지우는 근거는 **잠금 조회 응답**이다 — 이관 응답이 아니다. 서버 결정은 한 번 정해지면
             * 미정으로 돌아가지 않으므로, 응답이 어떤 순서로 와도 이 판단은 뒤집히지 않는다.
             * 서버가 미정인 동안은 브라우저 결정을 남겨 그것으로 판정한다(이관 전 캐시가 다시 와도 안 풀린다).
             */
            BROWSER_DECISION_KEYS.forEach((key) => {
                if (decisions[key] !== null && typeof browser[key] === 'boolean') {
                    clearClientLockDecision(key, account);
                    delete browser[key];
                }
            });
            const merged = mergeServerLockDecisions(sections as string[], decisions, browser);
            setLockedSectionsState(merged);
            setStoredLockedSections(merged);
            setSupport({ accountId: account, state: 'v3', decisions });
            return;
        }
        const merged = mergeClientLockDecisions(sections as string[], browser);
        setLockedSectionsState(merged);
        setStoredLockedSections(merged);
        setSupport({ accountId: account, state: 'legacy', decisions: EMPTY_SERVER_LOCK_DECISIONS });
    }, []);

    /*
     * 브라우저 결정 이관(1회) 조율 — 설계 §10-3. 요청 자체는 usePayrollAuth 가 보낸다
     * (이 provider 는 앱 전체를 감싸므로 api 모듈을 직접 들이지 않는다).
     * 페이지 수명 동안 (계정, 키) 당 1회(메모리 표식) — 실패해도 이 페이지에서는 다시 안 보낸다.
     * 이관 응답은 상태에 반영하지 않는다(잠금 조회를 다시 해서 그 응답으로만 반영한다).
     */
    const migrationAttemptedRef = useRef<Set<string>>(new Set());
    const claimLockMigration = useCallback((accountId: string, key: BrowserDecisionKey): boolean => {
        const mark = `${accountId}:${key}`;
        if (migrationAttemptedRef.current.has(mark)) return false;
        migrationAttemptedRef.current.add(mark);
        return true;
    }, []);

    /**
     * 잠금 대상 섹션 목록 업데이트.
     *
     * ⚠️ 여기 들어오는 값은 대부분 **서버 응답**이다. 서버는 자기 enum 밖의 키를
     *    걸러 내고 돌려주므로, 그대로 쓰면 조회가 한 번 돌 때마다 기프티콘·
     *    데일리 브리핑 잠금이 풀린다. 이 브라우저의 결정을 다시 얹는 이유다.
     */
    const setLockedSections = useCallback((sections: string[]) => {
        const merged = mergeClientLockDecisions(sections, readClientLockDecisions(currentAccountId()));
        setLockedSectionsState(merged);
        setStoredLockedSections(merged);
    }, []);

    /**
     * 서버가 모르는 섹션(기프티콘·데일리 브리핑)의 잠금을 이 브라우저에 기록하고 즉시 반영한다.
     *
     * 설정 화면이 저장 왕복 **전에** 부른다. 서버가 받아 주지 않는 키라 왕복이 없고,
     * 서버 저장이 실패해도 이 결정은 남아야 한다 (잠그려던 의도가 조용히 사라지면 안 된다).
     * 서버가 아는 키는 건드리지 않는다 — 그건 저장에 성공해야 반영된다.
     */
    const applyClientLockDecisions = useCallback(
        (sections: string[]) => {
            const decisions = recordClientLockDecisions(sections, currentAccountId());
            const merged = mergeClientLockDecisions(lockedSections, decisions);
            setLockedSectionsState(merged);
            setStoredLockedSections(merged);
        },
        [lockedSections],
    );

    /** 해당 섹션이 잠금 대상인지 확인 */
    const isSectionLocked = useCallback(
        (section: string): boolean => {
            const support = getLockSupport();
            /*
             * 스케줄 동작(생성·수정·삭제):
             *  · v3      — 서버 결정(합친 배열)
             *  · legacy  — 열림. 구서버는 이 잠금을 모른다(롤백 = 지금 동작). 승격 저장된 'schedule' 부모가
             *              배열에 있어도 보기 잠금일 뿐이라 동작 잠금으로 번지지 않게 여기서 끊는다.
             *  · unknown·error — 잠김(fail-closed). 누르면 확인 뒤 다시 판정한다(useScheduleActionLock).
             */
            if (isScheduleActionSection(section)) {
                if (support === 'unknown' || support === 'error') return true;
                if (support === 'legacy') return false;
                return isLockSectionLocked(lockedSections, section);
            }
            /* 손실 시그널·기프티콘은 계약 판정 전(unknown)이면 잠긴 것으로 본다 — fail-closed */
            if (isDecisionLockSection(section) && support === 'unknown') return true;
            return isLockSectionLocked(lockedSections, section);
        },
        [lockedSections, getLockSupport]
    );

    /** 인증 완료 — 한 번 인증하면 모든 잠금 섹션 30분간 해제 */
    const unlockSection = useCallback((section: string) => {
        const now = Date.now();
        setUnlockedAt(now);
        setStoredUnlockTime(now);
        setLastSection(section);
    }, []);

    /** 즉시 잠금 */
    const lockSection = useCallback(() => {
        setUnlockedAt(null);
        setLastSection(null);
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch {}
    }, []);

    /** 현재 인증 상태 */
    const isUnlocked = (() => {
        const storedTime = getStoredUnlockTime();
        if (storedTime == null) return false;
        return Date.now() - storedTime <= SESSION_DURATION_MS;
    })();

    /** 섹션이 30분 이내 인증 상태인지 체크 */
    const isSectionUnlocked = useCallback(
        (_section: string): boolean => {
            const storedTime = getStoredUnlockTime();

            if (storedTime == null) {
                return false;
            }

            const elapsed = Date.now() - storedTime;
            if (elapsed > SESSION_DURATION_MS) {
                return false;
            }
            return true;
        },
        []
    );

    // sessionStorage와 state 동기화를 렌더 외부에서 처리
    useEffect(() => {
        const storedTime = getStoredUnlockTime();
        if (storedTime == null && unlockedAt != null) {
            setUnlockedAt(null);
        } else if (storedTime != null && Date.now() - storedTime > SESSION_DURATION_MS) {
            setUnlockedAt(null);
            try {
                sessionStorage.removeItem(STORAGE_KEY);
            } catch {}
        }
    });

    return (
        <PayrollAuthContext.Provider value={{
            lastSection, unlockSection, lockSection, setLastSection,
            isSectionUnlocked, isUnlocked,
            isSectionLocked, lockedSections, setLockedSections, applyClientLockDecisions, syncAccount,
            getLockSupport, reportLockFetch,
            lockSupport: support, claimLockMigration
        }}>
            {children}
        </PayrollAuthContext.Provider>
    );
};

export const usePayrollAuthContext = () => {
    const ctx = useContext(PayrollAuthContext);
    if (!ctx) throw new Error('usePayrollAuthContext must be used within PayrollAuthProvider');
    return ctx;
};
